import { AlertTriangle, PackageOpen, SearchX } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

interface EmptyStatesProps {
  variant: "no-results" | "no-data" | "error";
  onClearFilters?: () => void;
  onRetry?: () => void;
}

export function EmptyStates({ variant, onClearFilters, onRetry }: EmptyStatesProps): JSX.Element {
  const { t } = useTranslation("inventory");

  if (variant === "error") {
    return (
      <div
        className="flex flex-col items-center gap-3 py-16 text-center"
        data-testid="inventory-error"
      >
        <AlertTriangle className="h-8 w-8 text-[var(--intent-danger-fg)]" aria-hidden />
        <p className="text-sm text-[var(--text-primary)]">{t("state.error")}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            data-testid="empty-retry"
            className="rounded border border-[var(--border-default)] px-3 py-1.5 text-sm font-medium hover:bg-[var(--surface-sunken)] focus-ring transition-colors"
          >
            {t("state.retry")}
          </button>
        )}
      </div>
    );
  }

  if (variant === "no-data") {
    return (
      <div
        className="flex flex-col items-center gap-3 py-16 text-center"
        data-testid="inventory-no-data"
      >
        <PackageOpen className="h-8 w-8 text-[var(--text-subtle)]" aria-hidden />
        <p className="text-sm font-medium text-[var(--text-primary)]">{t("state.no_data")}</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center gap-3 py-16 text-center"
      data-testid="inventory-empty"
    >
      <SearchX className="h-8 w-8 text-[var(--text-subtle)]" aria-hidden />
      <p className="text-sm font-medium text-[var(--text-primary)]">{t("state.no_results")}</p>
      <p className="text-sm text-[var(--text-subtle)]">{t("state.no_results_hint")}</p>
      {onClearFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          data-testid="empty-clear-filters"
          className="rounded border border-[var(--border-default)] px-3 py-1.5 text-sm font-medium hover:bg-[var(--surface-sunken)] focus-ring transition-colors"
        >
          {t("state.clear_filters")}
        </button>
      )}
    </div>
  );
}
